import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Check, Loader2, Sparkles, Crown, Rocket, Star, Zap, Shield, Users, BarChart3, Calendar, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const ClientSubscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [payPerJob, setPayPerJob] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or yearly

  useEffect(() => {
    fetchPlans();
    fetchCurrentPlan();

    // Handle success/cancel from Stripe.
    // On success we call /confirm/{session_id} FIRST so the subscription is
    // activated inline even if the Stripe webhook hasn't landed yet. This
    // prevents the "I paid, but my plan is still Free" race condition.
    if (searchParams.get('success') === 'true') {
      const sessionId = searchParams.get('session_id');
      const activatePlan = async () => {
        if (sessionId) {
          try {
            await api.post(`/monetization/client-subscriptions/confirm/${sessionId}`);
          } catch (err) {
            // Non-fatal: the webhook will still activate it eventually.
            console.warn('Confirm call failed; webhook will activate later:', err);
          }
        }
        toast.success('Subscription activated! Welcome to your new plan.');
        fetchCurrentPlan();
      };
      activatePlan();
    } else if (searchParams.get('cancelled') === 'true') {
      toast.info('Subscription cancelled — no charge was made.');
    }
  }, [searchParams]);

  const fetchPlans = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/plans');
      setPlans(res.data.plans || []);
      setPayPerJob(res.data.pay_per_job || null);
    } catch (err) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/status');
      setCurrentPlan(res.data);
    } catch {}
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const res = await api.post(`/monetization/client-subscriptions/subscribe/${planId}`, {
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(null);
    }
  };

  const handlePayPerJob = async () => {
    setSubscribing('pay_per_job');
    try {
      const res = await api.post('/monetization/job-post/checkout');
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process');
    } finally {
      setSubscribing(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return <Star className="w-6 h-6" />;
      case 'preferred': return <Rocket className="w-6 h-6" />;
      case 'premier': return <Crown className="w-6 h-6" />;
      case 'pro_plus': return <Zap className="w-6 h-6" />;
      case 'signature': return <Sparkles className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free': return 'from-slate-500 to-slate-600';
      case 'preferred': return 'from-cyan-500 to-teal-500';
      case 'premier': return 'from-emerald-500 to-green-600';
      case 'pro_plus': return 'from-violet-500 to-indigo-600';
      case 'signature': return 'from-amber-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPrice = (plan) => {
    return billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  };

  const getPriceLabel = (plan) => {
    // Iter-225w — surface the Free tier as "$0" with "Always free posting" sub-label
    if (plan.id === 'free' || (plan.price_monthly === 0 && plan.price_yearly === 0)) {
      return (
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-extrabold tracking-tight text-gray-900">$0</span>
            <span className="text-base text-gray-500 font-medium">/forever</span>
          </div>
          <p className="text-xs text-emerald-600 mt-2 font-semibold">Always free job posting</p>
        </div>
      );
    }
    if (billingCycle === 'yearly') {
      const monthlyEquiv = Math.round(plan.price_yearly / 12);
      return (
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-extrabold tracking-tight text-gray-900">${monthlyEquiv.toLocaleString()}</span>
            <span className="text-base text-gray-500 font-medium">/mo</span>
          </div>
          <p className="text-xs text-emerald-600 mt-2 font-semibold">
            ${plan.price_yearly.toLocaleString()}/yr billed annually · 12 months for the price of 10
          </p>
        </div>
      );
    }
    return (
      <div className="text-center">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-5xl font-extrabold tracking-tight text-gray-900">${plan.price_monthly.toLocaleString()}</span>
          <span className="text-base text-gray-500 font-medium">/mo</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          or ${plan.price_yearly?.toLocaleString()}/yr — 2 months free
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24 overflow-y-auto" data-testid="subscription-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/client/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          {currentPlan?.has_subscription && (currentPlan.plan_name || currentPlan.plan_id) && (
            <Badge className="bg-green-100 text-green-700">
              Current: {currentPlan.plan_name || currentPlan.plan_id}
            </Badge>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-8 px-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 mb-2">
            Choose Your Plan
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            <span className="font-semibold text-emerald-700">Posting opportunities on iEvntür is always free.</span>{' '}
            Upgrade anytime for featured visibility, automation, analytics, team access, workforce management, and premium recruiting support.
          </p>
        </div>

        {/* Unified pricing preview — purchasing opens at the coordinated launch */}
        <div className="mb-6 sm:mb-8 max-w-2xl mx-auto bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center" data-testid="unified-pricing-banner">
          <p className="text-sm text-emerald-800 font-medium">
            New unified iEvntür pricing. Annual plans give you 12 months for the price of 10.
          </p>
        </div>

        {/* Billing Toggle */}
        <div
          className="flex items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 bg-white border border-gray-200 rounded-full px-5 py-2.5 w-fit mx-auto shadow-sm"
          data-testid="billing-toggle"
        >
          <span className={`text-sm font-medium transition ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>
            Monthly
          </span>
          <Switch
            checked={billingCycle === 'yearly'}
            onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
            className="data-[state=checked]:bg-emerald-500"
            data-testid="billing-cycle-switch"
          />
          <span className={`text-sm font-medium transition ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
            Yearly
          </span>
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px] font-semibold">
            2 months free
          </Badge>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-5 lg:gap-4 mb-8 items-stretch">
          {plans.map((plan) => {
            const isPopular = !!plan.popular;
            const isCurrent = currentPlan?.plan_id === plan.id;
            return (
              <div
                key={plan.id}
                className={`relative ${isPopular ? 'md:-mt-4 md:mb-4' : ''}`}
                data-testid={`plan-card-wrapper-${plan.id}`}
              >
                {isPopular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"
                    data-testid="popular-banner"
                  >
                    <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-[11px] font-bold tracking-wide uppercase px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap">
                      <Crown className="w-3.5 h-3.5" />
                      Most Popular · Recommended
                    </div>
                  </div>
                )}
                <Card
                  className={`relative overflow-hidden h-full flex flex-col transition-all duration-300 ${
                    isPopular
                      ? 'border-2 border-emerald-500 shadow-2xl shadow-emerald-500/15 ring-4 ring-emerald-500/10'
                      : 'border border-gray-200 hover:shadow-lg hover:border-cyan-300'
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {/* Plan Header */}
                  <div className={`bg-gradient-to-r ${getPlanColor(plan.id)} p-5 text-white`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      {getPlanIcon(plan.id)}
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                    </div>
                    <p className="text-white/90 text-sm leading-snug">{plan.description}</p>
                  </div>

                  <CardContent className="p-6 flex flex-col flex-1">
                    {/* Price */}
                    <div className="mb-5 pb-5 border-b border-gray-100">
                      {getPriceLabel(plan)}
                    </div>

                    {/* Platform Fee (shown only when the plan exposes one) */}
                    {plan.platform_fee_percent != null && (
                      <div className="mb-5 bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Platform Fee</p>
                        <p className="text-2xl font-bold text-gray-900 mt-0.5">{plan.platform_fee_percent}%</p>
                      </div>
                    )}

                    {/* Features */}
                    <ul className="space-y-3 mb-6 flex-1">
                      {plan.max_featured_posts_per_month != null && (
                      <li className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">
                          {plan.max_featured_posts_per_month === -1 ? 'Unlimited' :
                            plan.max_featured_posts_per_month != null ? `Up to ${plan.max_featured_posts_per_month}` :
                            (plan.max_jobs_per_month === -1 ? 'Unlimited' : `Up to ${plan.max_jobs_per_month}`)}{' '}
                          {plan.max_featured_posts_per_month != null ? 'featured' : ''} job posts/month
                        </span>
                      </li>
                      )}
                      {plan.max_user_seats != null && (
                      <li className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">
                          {plan.max_user_seats === -1 ? 'Unlimited' : plan.max_user_seats} team member{plan.max_user_seats !== 1 && plan.max_user_seats !== -1 ? 's' : ''}
                          {plan.additional_user_price_usd > 0 && plan.max_user_seats !== -1 && (
                            <span className="block text-[11px] text-gray-500 mt-0.5">
                              Need more seats? Add additional team members for ${Number(plan.additional_user_price_usd).toFixed(0)} each.
                            </span>
                          )}
                        </span>
                      </li>
                      )}
                      {plan.id === 'free' && (
                        <li className="flex items-start gap-2 text-sm" data-testid="free-plan-local-only-hint">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Local market visibility only
                            <span className="block text-[11px] text-amber-700 mt-0.5">
                              Need nationwide reach? Upgrade this post for expanded visibility.
                            </span>
                          </span>
                        </li>
                      )}
                      {plan.features?.filter(f => !f.includes('job posts') && !f.includes('job post') && !f.includes('team member') && !f.includes('Additional team members') && !f.includes('Local job posting') && !f.includes('Local market')).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Subscribe Button */}
                    <Button
                      className={`w-full h-11 font-semibold text-sm transition-all ${
                        (plan.id !== 'free' && !plan.purchasable && !isCurrent)
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : isPopular
                          ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-500/25'
                          : isCurrent
                            ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                            : plan.id === 'free'
                              ? 'bg-slate-700 hover:bg-slate-800 text-white'
                              : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                      }`}
                      onClick={() => {
                        if (plan.id === 'free') { window.location.href = '/client/post-gig'; return; }
                        if (!plan.purchasable) return;
                        handleSubscribe(plan.id);
                      }}
                      disabled={subscribing === plan.id || isCurrent || (plan.id !== 'free' && !plan.purchasable)}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {subscribing === plan.id ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                      ) : isCurrent ? (
                        <><Check className="w-4 h-4 mr-2" />Current Plan</>
                      ) : plan.id === 'free' ? (
                        'Start Posting Free'
                      ) : plan.purchasable ? (
                        'Choose Plan'
                      ) : (
                        'Available at launch'
                      )}
                    </Button>

                    {plan.id !== 'free' && !plan.purchasable && !isCurrent && (
                      <p className="text-center text-[11px] text-gray-400 mt-2">
                        Public purchasing opens at the unified pricing launch
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Pay-Per-Job Option */}
        {payPerJob && (
          <Card className="mb-8 border-2 border-dashed border-gray-300" data-testid="pay-per-job-card">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pay-Per-Job</h3>
                    <p className="text-gray-500 text-sm">No subscription needed. Post individual jobs as needed.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">${payPerJob.price}</p>
                    <p className="text-xs text-gray-500">per job post</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handlePayPerJob}
                    disabled={subscribing === 'pay_per_job'}
                    className="border-gray-300"
                    data-testid="pay-per-job-btn"
                  >
                    {subscribing === 'pay_per_job' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Post a Job'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plan entitlements are managed by iEvntür's unified pricing system */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 max-w-2xl mx-auto" data-testid="entitlements-note">
            Every business plan includes always-free opportunity posting. Higher tiers add
            featured visibility, national reach, analytics, team access and premium recruiting
            support. Your plan's exact entitlements and seats are managed by iEvntür.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientSubscription;
